/* */
/**
 * @module actions/Share
 */
import { type Dispatch } from 'redux';
import { createAction } from 'typesafe-actions';

import { OVERLAY_SUBTYPES } from '../constants/Overlay';
import { messages as ShareMessages, ShareActions } from '../constants/Share';
import { PandoraTypes } from '../constants/SourceTypes';
import ViewActions from '../constants/ViewActions';
import { getInDomain } from '../domains/externalUtils';
import { getAnnotationsById } from '../lib/EntityUtils';
import { isRetired, isUnavailable } from '../lib/RightsUtils';
import Logger from '../tools/Logger';

import { type OpenModalCoachmarkAction, openModal, openModalCoachmark } from './Modal';
import { type CheckAndMaybeOpenOverlayAction } from './Overlay';

const log = Logger.child({ component: 'Share' });

/**
 * Action creator triggered by Share SourceCard item
 */

export const openShareModal =
  (pandoraId: $TSFixMe, shareParentSource: $TSFixMe = {}) =>
  (
    dispatch: Dispatch<OpenModalCoachmarkAction | CheckAndMaybeOpenOverlayAction>,
    getState: $TSFixMeFunction,
  ) => {
    let { shareParentSourceId, shareParentSourceType } = shareParentSource;

    if (!pandoraId && !shareParentSourceId) {
      log.error('Intent to share requires either a pandoraId or a shareParentSourceId');
      return;
    }

    const isThumbprintRadio =
      shareParentSourceId &&
      getInDomain('playback.utils.isThumbprintStationId', false)(getState(), shareParentSourceId) &&
      !pandoraId.startsWith(PandoraTypes.SONG);
    const isHybridStation =
      shareParentSourceId &&
      getInDomain('playback.utils.getStationIsAdvertiser', false)(getState(), shareParentSourceId);

    let childId =
      (pandoraId && pandoraId.endsWith(shareParentSourceId)) || isThumbprintRadio || isHybridStation
        ? null
        : pandoraId;

    const annotations = getAnnotationsById(getState(), pandoraId);
    const shareType = annotations && annotations.get('type');

    switch (shareType) {
      case PandoraTypes.EPISODE:
        childId = pandoraId;
        shareParentSourceId = annotations && annotations.get('podcastId');
        shareParentSourceType = PandoraTypes.PODCAST;
        break;
      case PandoraTypes.PODCAST:
        shareParentSourceId = pandoraId;
        shareParentSourceType = PandoraTypes.PODCAST;
        break;
    }

    if (isUnavailable(annotations) || isRetired(annotations)) {
      let message;
      switch (shareType) {
        case PandoraTypes.ALBUM:
          message = ShareMessages.cannotShareAlbum;
          break;
        case PandoraTypes.SONG:
          message = ShareMessages.cannotShareSong;
          break;
        case PandoraTypes.PLAYLIST:
          message = ShareMessages.cannotSharePlaylist;
          break;
        case PandoraTypes.EPISODE:
          message = ShareMessages.cannotSharePodcast;
          break;
        case PandoraTypes.PODCAST:
          message = ShareMessages.cannotSharePodcast;
          break;
        default:
          message = ShareMessages.cannotShareDefault;
          break;
      }
      return dispatch(openModalCoachmark(message));
    }

    return dispatch(
      openModal({
        modalType: OVERLAY_SUBTYPES.MODALSHARE,
        childId,
        parentSourceId: shareParentSourceId,
        parentSourceType: shareParentSourceType,
      }),
    );
  };

interface ShowShareCoachmarkParams extends $TSFixMe {}

export const showShareCoachmark = createAction(
  ShareActions.SHOW_SHARE_COACHMARK,
  (body: ShowShareCoachmarkParams) => body,
)<ShowShareCoachmarkParams>();
export type ShowShareCoachmarkAction = ReturnType<typeof showShareCoachmark>;

interface ShareOptionClickedParams {
  shareMethod: $TSFixMe;
  twitter: $TSFixMe;
  twitterHandle: $TSFixMe;
  facebook: $TSFixMe;
  sourceId: $TSFixMe;
}

export const clickedShareOption = createAction(
  ShareActions.SHARE_OPTION_CLICKED,
  (params: ShareOptionClickedParams) => params,
)<ShareOptionClickedParams>();
export type ShareOptionClickedAction = ReturnType<typeof clickedShareOption>;

interface ShowedShareModalParams {
  parentSource: $TSFixMe;
  sourceId: $TSFixMe;
}

export const showedShareModal = createAction(
  ViewActions.SHOWED_SHARE_MODAL,
  ({ parentSource, sourceId }: ShowedShareModalParams) => ({ sourceId, parentSource }),
)<ShowedShareModalParams>();
export type ShowedShareModalAction = ReturnType<typeof showedShareModal>;
